import * as TemplateTypes from 'types/template.types';
import * as GameTypes from 'types/game.types';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { fromLocalStorage, toLocalStorage } from 'util/util';
import { Achievements, getCrossGameStats, getInGameStats, CrossGameStats, InGameStats, AchievementDefinition } from '../achievements';
import { Themes } from 'src/themes-v2';

interface AchievementPersistedState {
    credits: number,
    completed: GameTypes.AchievementKey[],
    unlockedThemes: TemplateTypes.ThemeKey[],
    crossGameStats: ReturnType<typeof getCrossGameStats>
}


/**
 * NOTE: The ref syntax below seen here `ref() as Ref<>` is apparently the way a ref has to be defined when
 * the type is a generic.
 */
const crossGameStorageKey = 'achievement-cross-game-stats';
const completedStorageKey = 'achievement-completed';
const useAchievmentStore = defineStore('achievement-store', () => {
    const persisted = fromLocalStorage<AchievementPersistedState>('achievement-store', {
        credits: 25,
        completed: [],
        unlockedThemes: [],
        crossGameStats: getCrossGameStats()
    })



    const credits = ref<number>(persisted.credits);
    const completed = ref<GameTypes.AchievementKey[]>(persisted.completed);
    const unlockedThemes = ref<TemplateTypes.ThemeKey[]>(persisted.unlockedThemes);
    const crossGameStats = ref(fromLocalStorage(crossGameStorageKey, persisted.crossGameStats));
    const inGameStats = ref(getInGameStats());
    const registry = ref(Achievements);

    const persist = () => {
        toLocalStorage<AchievementPersistedState>('achievement-store', {
            credits: credits.value,
            completed: completed.value,
            unlockedThemes: unlockedThemes.value,
            crossGameStats: crossGameStats.value
        })
    }

    /**
     * Return `true` if the given achievement key has been recorded as completed
     * 
     * @param key - the achievement key as defined in the registry
     * @returns 
     */
    const isComplete = (key: GameTypes.AchievementKey) => completed.value.includes(key);

    /**
     * Retrieve an achievement by its key
     * 
     * @param key - the achievement key as defined in the registry
     * @returns 
     */
    const getAchievement = (key: GameTypes.AchievementKey) => registry.value[key];

    /**
     * Resets in-game stats only. Should be called whenever a new match starts.
     */
    const resetInGameStats = () => inGameStats.value = getInGameStats();
    
    /**
     * Resets all achievement completion states and all stats back to defaults
     */
    const reset = () => {
        completed.value = [];
        crossGameStats.value = getCrossGameStats();
        inGameStats.value = getInGameStats();
        persist();
    }

    /**
     * Shallow merge tracking stats and return any just-completed achievements.
     * 
     * @param stats - partial stats object
     */
    const updateCrossGameStats = (updater: (stats: CrossGameStats) => Partial<typeof crossGameStats.value>) => {
        crossGameStats.value = {
            ...crossGameStats.value,
            ...updater(crossGameStats.value)
        }
        persist();
    }

    /**
     * Shallow merge match stats and return any just-completed achievements.
     */
    const updateInGameStats = (updater: (stats: InGameStats) => Partial<typeof inGameStats.value>) => {
        inGameStats.value = {
            ...inGameStats.value,
            ...updater(inGameStats.value)
        }
    }

    const getStats = () => {
        return {
            crossGame: { ...crossGameStats.value},
            inGame: { ...inGameStats.value }
        }
    }

    const setCompleted = (key: GameTypes.AchievementKey, achievement: TemplateTypes.Achievement<GameTypes.AchievementKey, CrossGameStats, InGameStats>) => {
        if (registry.value[key]){
            completed.value.push(key);
            credits.value += achievement.credits;
            persist();
        }
    }

    const getCredits = () => credits.value;
    const addCredits = (count: number) => {
        credits.value += count;
        persist();
    }

    const unlockTheme = (themeKey: TemplateTypes.ThemeKey) : boolean => {
        let theme = Themes[themeKey];
        if (!unlockedThemes.value.includes(themeKey) && credits.value >= (theme.credits ?? 0)) {
            credits.value -= theme.credits ?? 0;
            unlockedThemes.value.push(themeKey);
            persist();
            return true;
        }
        return false;
    }

    const isThemeUnlocked = (themeKey: TemplateTypes.ThemeKey) : boolean => {
        let theme = Themes[themeKey];
        return !theme.credits || theme.credits === 0 || unlockedThemes.value.includes(themeKey)
    }

    const test = () : AchievementDefinition[] => {
        let results : TemplateTypes.Achievement<GameTypes.AchievementKey, CrossGameStats, InGameStats>[] = [];
        Object.entries(registry.value).forEach(entry => {
            let key = <GameTypes.AchievementKey> entry[0], achievement = entry[1];
            if (!completed.value.includes(key) && achievement.test(crossGameStats.value, inGameStats.value)){
                results.push(achievement);
                setCompleted(key, achievement); //Update our internal completion list / credits
            }
        });
        return results;
    }

    const getProgress = (key: GameTypes.AchievementKey) => {
        return Achievements[key].progress(
            crossGameStats.value, 
            inGameStats.value,
            completed.value
        )
    }

    return { 
        isComplete, 
        getAchievement, 
        resetInGameStats, 
        reset, 
        updateCrossGameStats,  
        updateInGameStats,
        getStats,
        test,
        getCredits,
        addCredits,
        unlockTheme,
        isThemeUnlocked,
        getProgress
    };
});

export { useAchievmentStore };
